/**
 * Small, reusable feedback affordances that every AI artifact can drop in:
 *
 * - `AiFeedbackControl` — thumbs up / down for an `ai_trace_id` (down reveals
 *   an optional note box).
 * - `ReportProblemButton` — a "Report a problem" affordance that captures a
 *   short message (and optional `ai_trace_id` + context).
 *
 * Both are dependency-light (inline styles + lucide icons already in the app)
 * and route through the analytics singleton, so they work with or without an
 * `AnalyticsProvider` in scope.
 */

import { useState, type CSSProperties } from 'react'
import { Flag, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useAnalytics } from './provider'
import type { AnalyticsProps } from './types'

export function AiFeedbackControl(props: {
  aiTraceId: string
  surface: string
  workbookId?: string | null
  props?: AnalyticsProps
  size?: number
  className?: string
}) {
  const { aiTraceId, surface, workbookId, size = 14, className } = props
  const analytics = useAnalytics()
  const [verdict, setVerdict] = useState<'up' | 'down' | null>(null)
  const [noteOpen, setNoteOpen] = useState(false)
  const [note, setNote] = useState('')
  const [noteSent, setNoteSent] = useState(false)

  const disabled = !aiTraceId

  const send = (v: 'up' | 'down', noteText?: string) => {
    if (disabled) return
    analytics.aiFeedback({
      ai_trace_id: aiTraceId,
      verdict: v,
      note: noteText && noteText.trim() ? noteText.trim() : null,
      surface,
      workbook_id: workbookId ?? null,
      props: props.props,
    })
  }

  const onUp = () => {
    setVerdict('up')
    setNoteOpen(false)
    send('up')
  }

  const onDown = () => {
    setVerdict('down')
    setNoteOpen(true)
    setNoteSent(false)
    send('down')
  }

  const submitNote = () => {
    if (!note.trim()) {
      setNoteOpen(false)
      return
    }
    send('down', note)
    setNoteSent(true)
    setNoteOpen(false)
    setNote('')
  }

  const btnStyle = (active: boolean): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    border: '1px solid',
    borderColor: active ? '#2563eb' : '#d1d5db',
    color: active ? '#2563eb' : '#6b7280',
    background: active ? '#eff6ff' : 'transparent',
    borderRadius: 6,
    padding: '2px 6px',
    cursor: disabled ? 'default' : 'pointer',
    fontSize: 11,
    lineHeight: 1,
  })

  return (
    <span className={className} data-ai-trace-id={aiTraceId} style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          aria-label="This answer was helpful"
          aria-pressed={verdict === 'up'}
          title="Helpful"
          disabled={disabled}
          onClick={onUp}
          style={btnStyle(verdict === 'up')}
        >
          <ThumbsUp size={size} />
        </button>
        <button
          type="button"
          aria-label="This answer was not helpful"
          aria-pressed={verdict === 'down'}
          title="Not helpful"
          disabled={disabled}
          onClick={onDown}
          style={btnStyle(verdict === 'down')}
        >
          <ThumbsDown size={size} />
        </button>
        {noteSent ? <span style={{ fontSize: 11, color: '#16a34a' }}>Thanks for the detail</span> : null}
      </span>
      {noteOpen ? (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What was wrong? (optional)"
            rows={2}
            style={{ fontFamily: 'inherit', fontSize: 11, width: 240, resize: 'vertical' }}
          />
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button type="button" onClick={submitNote} style={{ fontSize: 11, cursor: 'pointer' }}>
              Send
            </button>
            <button
              type="button"
              onClick={() => {
                setNoteOpen(false)
                setNote('')
              }}
              style={{ fontSize: 11, cursor: 'pointer' }}
            >
              Skip
            </button>
          </span>
        </span>
      ) : null}
    </span>
  )
}

export function ReportProblemButton(props: {
  surface: string
  aiTraceId?: string | null
  context?: AnalyticsProps
  label?: string
  size?: number
  className?: string
}) {
  const { surface, aiTraceId = null, context, label = 'Report a problem', size = 13, className } = props
  const analytics = useAnalytics()
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [sent, setSent] = useState(false)

  const submit = () => {
    const text = message.trim()
    if (!text) return
    analytics.reportBug({
      surface,
      ai_trace_id: aiTraceId,
      message: text,
      context,
    })
    setSent(true)
    setOpen(false)
    setMessage('')
  }

  if (sent && !open) {
    return (
      <span className={className} style={{ fontSize: 11, color: '#16a34a', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Flag size={size} /> Report sent
      </span>
    )
  }

  return (
    <span className={className} style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          border: '1px solid #d1d5db',
          color: '#6b7280',
          background: 'transparent',
          borderRadius: 6,
          padding: '2px 6px',
          cursor: 'pointer',
          fontSize: 11,
          lineHeight: 1,
        }}
      >
        <Flag size={size} /> {label}
      </button>
      {open ? (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe what went wrong…"
            rows={3}
            style={{ fontFamily: 'inherit', fontSize: 11, width: 260, resize: 'vertical' }}
          />
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button type="button" onClick={submit} disabled={!message.trim()} style={{ fontSize: 11, cursor: 'pointer' }}>
              Send report
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setMessage('')
              }}
              style={{ fontSize: 11, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </span>
        </span>
      ) : null}
    </span>
  )
}
